import React, { useState } from "react";
import { X } from "lucide-react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";

const blank = { name: "", date: "", end_date: "", text: "", code: "", pct: 10, bg_color: "#16243b", text_color: "#ffffff", repeat_yearly: false };
const input = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]";

export const EventDialog = ({ event, onClose, onSaved }) => {
  const [f, setF] = useState(event ? { ...blank, ...event.raw } : blank);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, pct: parseFloat(f.pct) || 0 };
      if (event) await api.put(`/business/events/${event.event_id}`, body);
      else await api.post("/business/events", body);
      toast({ title: event ? "Event updated" : "Event added" });
      onSaved();
    } catch (err) {
      toast({ title: "Could not save event", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[520px] p-6 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-[#16243b]">{event ? "Edit event" : "Add your own event"}</h3>
          <button type="button" onClick={onClose}><X size={18} className="text-[#5a6b80]" /></button>
        </div>
        <input required value={f.name} onChange={set("name")} placeholder="Event name, e.g. Summer Fair" data-testid="event-name" className={input} />
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-[#5a6b80]">Date<input required type="date" value={f.date} onChange={set("date")} data-testid="event-date" className={input} /></label>
          <label className="text-xs text-[#5a6b80]">End date (optional)<input type="date" value={f.end_date || ""} min={f.date} onChange={set("end_date")} data-testid="event-end-date" className={input} /></label>
        </div>
        <input value={f.text} onChange={set("text")} placeholder="Banner text, e.g. Summer Fair weekend – 10% off vouchers" data-testid="event-text" className={input} />
        <div className="grid grid-cols-2 gap-3">
          <input value={f.code} onChange={set("code")} placeholder="Discount code (optional)" data-testid="event-code" className={`${input} font-mono uppercase`} />
          <label className="flex items-center gap-2 text-sm text-[#16243b]">
            <input type="number" min="0" max="100" value={f.pct} onChange={set("pct")} data-testid="event-pct" className={`${input} w-20`} /> % off
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-[#16243b]">
          <label className="flex items-center gap-2"><input type="color" value={f.bg_color} onChange={set("bg_color")} data-testid="event-bg" className="w-10 h-9 rounded border border-gray-200" /> Banner</label>
          <label className="flex items-center gap-2"><input type="color" value={f.text_color} onChange={set("text_color")} data-testid="event-fg" className="w-10 h-9 rounded border border-gray-200" /> Text</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.repeat_yearly} onChange={set("repeat_yearly")} data-testid="event-repeat" /> Repeat every year</label>
        </div>
        <div className="rounded-xl px-4 py-3 text-sm font-semibold" style={{ background: f.bg_color, color: f.text_color }}>{f.text || f.name || "Banner preview"}</div>
        <button disabled={saving} data-testid="event-save" className="gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60">{saving ? "Saving…" : "Save event"}</button>
      </form>
    </div>
  );
};
